import React, { useState } from "react";
import { usePanes } from "../../../../main/panes/usePanes";
import DocumentPane from "../../../../main/panes/documentPane/DocumentPane";
import { Box, TableCell, TableRow } from "@mui/material";
import { DotLegalButton, DotLegalEmptyState, DotLegalPageHeader, DotLegalPaper, DotLegalTable, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import FilterBar from "../../filterBar/FilterBar.tsx";
import { PreviewableDocument } from "../DocumentPreview.types.ts";
import { buildDocumentPreviewUrl } from "../DocumentPreview.urls.ts";
import DocumentAccessCell from "../../documentAccessCell/DocumentAccessCell.tsx";
import { useAdvancedDocumentSearch } from "./AdvancedDocumentSearch.hooks.ts";
import { useTranslation } from "../../../../localization/useTranslation.ts";
import { DocumentEntityType, mapDocumentEntityTypeToEntityType } from "../../search/SearchResultList.types.ts";
import GlobalSearchIntroSvg from "../../../../main/content/topBar/global-search-intro.svg?react";
import SearchEmptyStateSvg from "../../../../main/content/topBar/search-empty-state.svg?react";
import { searchResultMarkStyle } from "@common/components/search/DocumentSearchResultList.tsx";
import { isNullOrWhitespace, setFirstLetterToLowerCase } from "@common/stringOperations.ts";
import {
    AdvancedDocumentSearchResultViewModel,
    AdvancedDocumentSortColumn,
} from "@common/components/documentPreview/advancedDocumentSearch/AdvancedDocumentSearch.types.ts";
import { whiteExportIcon } from "@common/icons.tsx";
import { isPreviewSupported } from "@common/components/documentPreview/DocumentPreview.hooks.ts";
import ListPageToolbar from "@common/components/listPageToolbar/ListPageToolbar.tsx";

export interface AdvancedDocumentSearchProps {}

function AdvancedDocumentSearch() {
    const { translateString, translateDate } = useTranslation();
    const {
        filterSearch,
        isLoading,
        data,
        mapSearchFilter,
        getFilterDataOptions,
        hasSearchCriteria,
        onDownloadClick,
        isDownloadingDocument,
        onTableSortingClick,
        onExportDocumentSearchClick,
        isExporting,
        hasFilterValues,
        onClearFilter,
        onSearchStringChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
    } = useAdvancedDocumentSearch();
    const { open: openPane } = usePanes();

    const showIntro = !hasSearchCriteria;
    const showEmptyResults = hasSearchCriteria && !isLoading && (!data?.results || data.results.length === 0);
    const showTable = hasSearchCriteria && data?.results && data.results.length > 0;

    function getHeaders(): Array<ITableHeader<AdvancedDocumentSearchResultViewModel>> {
        return [
            { property: "entityType", text: translateString("documentArea"), align: "left", notSortable: true },
            {
                property: "entityTitle",
                text: translateString("entity"),
                align: "left",
            },
            { property: "responsible", text: translateString("responsible"), align: "left", notSortable: true },
            { property: "documentTitle", text: translateString("documentName"), align: "left" },
            { property: "fileName", text: translateString("fileName"), align: "left", notSortable: true },
            { property: "documentDate", text: translateString("date"), align: "left" },
            { property: "documentVersionId", text: translateString("version"), align: "left", notSortable: true },
            { property: null, text: translateString("content"), align: "left", notSortable: true },
            { property: null, text: translateString("accessDocument"), align: "left", notSortable: true },
        ];
    }

    function handleRowClick(row: AdvancedDocumentSearchResultViewModel) {
        openPane(DocumentPane, {
            documents: [mapToPreviewableDocument(row)],
            initialSearchText: filterSearch.searchString,
        });
    }

    function mapToPreviewableDocument(doc: AdvancedDocumentSearchResultViewModel): PreviewableDocument {
        return {
            documentId: doc.documentId,
            documentVersionId: doc.documentVersionId,
            entityType: mapDocumentEntityTypeToEntityType(doc.entityType),
            entityId: doc.entityId,
            fileName: doc.fileName ?? "",
            name: doc.documentTitle ?? "",
            entityTitle: doc.entityTitle,
        };
    }

    function mapSortColumnToEnum(column: keyof AdvancedDocumentSearchResultViewModel | null): AdvancedDocumentSortColumn {
        switch (column) {
            case "entityTitle":
                return AdvancedDocumentSortColumn.EntityTitle;
            case "documentDate":
                return AdvancedDocumentSortColumn.DocumentDate;
            case "documentTitle":
                return AdvancedDocumentSortColumn.DocumentTitle;
        }

        return AdvancedDocumentSortColumn.DocumentTitle;
    }

    const canPreviewDocument = (row: AdvancedDocumentSearchResultViewModel) => {
        return !isNullOrWhitespace(row.fileName) && isPreviewSupported(row.fileName!);
    };

    const getPreviewHref = (row: AdvancedDocumentSearchResultViewModel): string | undefined => {
        if (!canPreviewDocument(row)) return undefined;
        return buildDocumentPreviewUrl(mapToPreviewableDocument(row), mapDocumentEntityTypeToEntityType(row.entityType), row.entityId);
    };

    return (
        <>
            <DotLegalPageHeader breadCrumbs={[{ name: translateString("advancedDocumentSearch") }]} />
            <DotLegalPaper fullHeight>
                <ListPageToolbar
                    leftContent={
                        <FilterBar
                            isLoading={false}
                            filterData={getFilterDataOptions()}
                            fetchFilterData
                            onMenuClose={(options) => mapSearchFilter(options)}
                            onRemove={(options) => mapSearchFilter(options)}
                            pinnedFilterIds={pinnedFilterIds}
                            onPinnedFilterIdsChange={onPinnedFilterIdsChange}
                            onClearFilter={hasFilterValues ? () => onClearFilter() : undefined}
                            clearFilterText={translateString("clearFilter")}
                            searchValue={filterSearch?.searchString ?? ""}
                            searchLabel={translateString("contentSearch")}
                            onSearchChange={onSearchStringChange}
                        />
                    }
                    rightContent={
                        <DotLegalButton
                            disabled={!showTable}
                            onClick={onExportDocumentSearchClick}
                            buttonType={"primary"}
                            isLoading={isExporting}
                            endIcon={whiteExportIcon}
                        >
                            {translateString("exportList")}
                        </DotLegalButton>
                    }
                />

                {(showIntro || showEmptyResults) && (
                    <DotLegalEmptyState
                        noFixedHeight
                        icon={showIntro ? <GlobalSearchIntroSvg /> : <SearchEmptyStateSvg />}
                        title={showIntro ? translateString("advancedSearchIntroTitle") : translateString("advancedSearchEmptyTitle")}
                        text={showIntro ? translateString("advancedSearchIntroText") : translateString("advancedSearchEmptyText")}
                    />
                )}

                {(showTable || isLoading) && (
                    <DotLegalTable
                        headers={getHeaders()}
                        defaultOrderBy={"documentTitle"}
                        defaultOrder={filterSearch.sortingDirection}
                        isLoading={isLoading}
                        data={data?.results}
                        emptyText={translateString("noResults")}
                        paginationLabelOf={translateString("labelOf")}
                        onChange={(e) => {
                            onTableSortingClick(e.order, mapSortColumnToEnum(e.property), e.pageNo);
                        }}
                        rowsPerPage={50}
                        disableFilterAndSortAndSlicing
                        totalNumberOfRows={data?.totalCount ?? 0}
                        clickableRows
                        renderRow={(row) => {
                            const isPreviewable = canPreviewDocument(row);
                            const previewHref = isPreviewable ? getPreviewHref(row) : undefined;
                            return (
                                <TableRow key={row.id} onClick={isPreviewable ? () => handleRowClick(row) : undefined} sx={{ cursor: "pointer" }}>
                                    <TableCell>{translateString(setFirstLetterToLowerCase(DocumentEntityType[row.entityType].toString()))}</TableCell>
                                    <TableCell>{row.entityTitle}</TableCell>
                                    <TableCell>{row.responsible ?? undefined}</TableCell>
                                    <TableCell>{row.documentTitle}</TableCell>
                                    <TableCell>{row.fileName}</TableCell>
                                    <TableCell>{translateDate(new Date(row.documentDate))}</TableCell>
                                    <TableCell>{row.versionNumber}</TableCell>
                                    <TableCell>
                                        {row.contentSnippets.length > 0 && (
                                            <Box
                                                sx={(theme) => ({
                                                    color: theme.palette.text.secondary,
                                                    fontSize: "0.75rem",
                                                    display: "inline",
                                                    lineHeight: 1.5,
                                                    "& mark": {
                                                        ...searchResultMarkStyle,
                                                    },
                                                })}
                                                dangerouslySetInnerHTML={{ __html: row.contentSnippets[0].text }}
                                            />
                                        )}
                                    </TableCell>
                                    <DocumentAccessCell
                                        documentId={row.documentId}
                                        link={row.isLink ? row.link : undefined}
                                        downloadingDocument={isDownloadingDocument}
                                        isPreviewable={isPreviewable}
                                        previewHref={previewHref}
                                        onDownloadClick={() =>
                                            onDownloadClick(
                                                row.documentId,
                                                row.documentVersionId!,
                                                row.fileName!,
                                                row.entityId,
                                                mapDocumentEntityTypeToEntityType(row.entityType),
                                                row.isLink ? row.link : undefined
                                            )
                                        }
                                        onPreviewClick={() => handleRowClick(row)}
                                    />
                                </TableRow>
                            );
                        }}
                    />
                )}
            </DotLegalPaper>
        </>
    );
}

export default AdvancedDocumentSearch;
